/**
 * Bringing mail in from another IMAP server (PRD Phase 5.5). An administrator
 * lists mailboxes as CSV; the migrator copies each one, and can copy again —
 * a top-up while people still use the old server, then a final pass once the
 * MX record has moved. Finish deletes the stored source password.
 *
 * The same panel serves the platform console and webmail's Organisation
 * area, through `AdminWhereContext`.
 */

import { useContext, useEffect, useState } from 'react';
import type {
  MigrationBatch,
  MigrationCreated,
  MigrationJob,
  MigrationList,
  MigrationReport,
  MigrationStatus,
} from 'zitimail-shared-types';

import { api, ApiError, describeError } from './api';
import { AdminWhereContext, size } from './mailboxes';
import { ActionButton, Badge, Loaded, useLoad, when } from './ui';

const POLL_MS = 5000;

const STATUS_LABEL: Record<MigrationStatus, string> = {
  queued: 'waiting',
  running: 'copying',
  synced: 'copied',
  failed: 'failed',
  cancelled: 'cancelled',
  done: 'finished',
};

const STATUS_TONE: Record<MigrationStatus, 'ok' | 'warn' | 'crit' | 'neutral'> = {
  queued: 'neutral',
  running: 'warn',
  synced: 'ok',
  failed: 'crit',
  cancelled: 'neutral',
  done: 'ok',
};

/** Still moving: worth asking again in a few seconds. */
export function inMotion(job: MigrationJob): boolean {
  return job.status === 'queued' || job.status === 'running';
}

/** Reloads every five seconds while `on`. */
export function usePoll(on: boolean, reload: () => void): void {
  useEffect(() => {
    if (!on) {
      return undefined;
    }
    const timer = setInterval(reload, POLL_MS);
    return () => clearInterval(timer);
  }, [on, reload]);
}

function percent(copied: number, total: number): number {
  return total > 0 ? Math.min(100, Math.floor((copied / total) * 100)) : 0;
}

export function StatusBadge({ job }: { job: MigrationJob }) {
  const label =
    job.status === 'running' && job.phase === 'final'
      ? 'final pass'
      : job.status === 'running' && job.phase === 'topup'
        ? 'copying new mail'
        : STATUS_LABEL[job.status];
  return <Badge tone={STATUS_TONE[job.status]}>{label}</Badge>;
}

/** What a job is waiting for, in a sentence, or null when the badge says it all. */
export function statusNote(job: MigrationJob): string | null {
  if (job.status === 'queued') {
    return job.attempts > 0 && job.last_error
      ? `Will try again at ${when(job.next_attempt_at)}.`
      : 'Starts soon.';
  }
  if (job.status === 'failed') {
    return `Gave up after ${job.attempts} ${job.attempts === 1 ? 'try' : 'tries'}.`;
  }
  if (job.status === 'cancelled' || job.status === 'done') {
    return 'Closed. The password for the old server is deleted.';
  }
  return null;
}

export function Progress({ job }: { job: MigrationJob }) {
  if (job.messages_total === 0) {
    return <span className="adm-muted">{job.status === 'running' ? 'Counting…' : '—'}</span>;
  }
  const pct = percent(job.messages_copied, job.messages_total);
  return (
    <span className="mig-progress">
      <progress
        max={job.messages_total}
        value={job.messages_copied}
        aria-label={`Copied ${job.messages_copied} of ${job.messages_total} messages`}
      />{' '}
      <span className="adm-small">
        {job.messages_copied.toLocaleString()} of {job.messages_total.toLocaleString()} ({pct}%)
      </span>
    </span>
  );
}

/** An error, the first line or so, with the rest a click away. */
export function ErrorText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 120;
  return (
    <span className="adm-error">
      {open || !long ? text : `${text.slice(0, 120)}…`}{' '}
      {long && (
        <button type="button" className="adm-copy" onClick={() => setOpen(!open)}>
          {open ? 'Less' : 'Show all'}
        </button>
      )}
    </span>
  );
}

/** "12 mailboxes · 9 copied · 1 failed · 87% of messages". */
function rollup(batch: MigrationBatch): string {
  const parts = [`${batch.jobs} ${batch.jobs === 1 ? 'mailbox' : 'mailboxes'}`];
  const counts: Array<[number, string]> = [
    [batch.running, 'copying'],
    [batch.queued, 'waiting'],
    [batch.synced, 'copied'],
    [batch.failed, 'failed'],
    [batch.done, 'finished'],
    [batch.cancelled, 'cancelled'],
  ];
  for (const [n, label] of counts) {
    if (n > 0) {
      parts.push(`${n} ${label}`);
    }
  }
  if (batch.messages_total > 0) {
    parts.push(`${percent(batch.messages_copied, batch.messages_total)}% of messages`);
  }
  return parts.join(' · ');
}

// ─── The panel ───────────────────────────────────────────────────────────────

export function MigrationsPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<MigrationList>(`${base(slug)}/migrations`, { signal }),
    `migrations:${slug}`,
  );
  // Also after a failed refresh (a restart, a dropped connection): otherwise
  // the panel would stay on the error until the page was reloaded.
  const moving =
    load.state === 'error' || (load.state === 'ready' && load.data.jobs.some(inMotion));
  usePoll(moving, reload);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  return (
    <section className="adm-panel">
      <h3>Import mail from another server</h3>
      <p className="adm-muted">
        Copies each mailbox from the old IMAP server into its mailbox here. Nothing on the old
        server is changed or deleted. To move without losing mail:
      </p>
      <ol className="adm-muted adm-small mig-steps">
        <li>Start the import now, while people still use the old server.</li>
        <li>Top up as often as you like; each pass copies only what is new.</li>
        <li>Move the domain&apos;s MX record here, then run the final pass.</li>
        <li>Check the counts for each mailbox.</li>
        <li>Finish, which deletes the stored password for the old server.</li>
      </ol>
      {active && <NewImport slug={slug} onStarted={reload} />}
      <Loaded load={load}>
        {({ batches, jobs }) =>
          batches.length === 0 ? (
            <p className="adm-muted">No imports yet.</p>
          ) : (
            <div className="mig-batches">
              {batches.map((batch, index) => {
                const isOpen = open[batch.id] ?? index === 0;
                const mine = jobs.filter((j) => j.batch_id === batch.id);
                return (
                  <div key={batch.id} className="mig-batch">
                    <div className="adm-panel-head">
                      <h4>
                        {batch.name}{' '}
                        {batch.mode === 'self' && <Badge tone="neutral">own import</Badge>}
                      </h4>
                      <button
                        type="button"
                        className="adm-copy"
                        aria-expanded={isOpen}
                        onClick={() => setOpen({ ...open, [batch.id]: !isOpen })}
                      >
                        {isOpen ? 'Hide mailboxes' : 'Show mailboxes'}
                      </button>
                    </div>
                    <p className="adm-small adm-muted">
                      {rollup(batch)} · started {when(batch.created_at)} by {batch.created_by}
                    </p>
                    {isOpen && (
                      <JobTable slug={slug} jobs={mine} active={active} onChanged={reload} />
                    )}
                  </div>
                );
              })}
            </div>
          )
        }
      </Loaded>
    </section>
  );
}

// ─── Starting a batch ────────────────────────────────────────────────────────

const CSV_HINT = 'source_user,source_host,password,dest_email[,port,security,kind]';
const CSV_EXAMPLE = 'jane@oldmail.example,imap.oldmail.example,her-password,jane@acme.example';

function NewImport({ slug, onStarted }: { slug: string; onStarted: () => void }) {
  const { base } = useContext(AdminWhereContext);
  const [name, setName] = useState('');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MigrationCreated | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="mig-new"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        setResult(null);
        api<MigrationCreated>(`${base(slug)}/migrations`, {
          method: 'POST',
          body: { name: name.trim(), csv },
        })
          .then((made) => {
            setResult(made);
            // The list holds passwords; nothing is left on screen once it is in.
            setCsv('');
            setName('');
            onStarted();
          })
          .catch((e: unknown) => {
            const body = e instanceof ApiError ? (e.body as Partial<MigrationCreated>) : undefined;
            if (body && Array.isArray(body.errors)) {
              setResult({ batch_id: body.batch_id ?? '', created: 0, errors: body.errors });
            } else {
              setError(describeError(e));
            }
          })
          .finally(() => setBusy(false));
      }}
    >
      <h4>New import</h4>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name (optional), e.g. Sales team"
        aria-label="Import name"
      />
      <label className="adm-small adm-muted" htmlFor="mig-csv">
        One mailbox per line: <span className="adm-mono">{CSV_HINT}</span>. Port, security (ssl or
        starttls; ssl on 993 if left out) and kind (imap, gmail or office365) are optional. A header
        line is fine.
      </label>
      <textarea
        id="mig-csv"
        rows={6}
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder={CSV_EXAMPLE}
        aria-label="Mailboxes to import (CSV)"
        className="adm-mono"
        spellCheck={false}
      />
      <div className="adm-inline">
        <label className="adm-small">
          Read a .csv file{' '}
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                void file.text().then(setCsv);
              }
              e.target.value = '';
            }}
          />
        </label>
        <button type="submit" className="btn" disabled={busy || !csv.trim()}>
          {busy ? 'Starting…' : 'Start import'}
        </button>
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
      </div>
      <p className="adm-muted adm-small">
        Passwords are stored encrypted until the import is finished or cancelled, then deleted.
      </p>
      {result && (
        <div role="status" className="mig-result">
          <strong className={result.created > 0 ? 'adm-ok' : 'adm-error'}>
            {result.created > 0 ? `${result.created} started` : 'Nothing was started'}
          </strong>
          {result.errors.length > 0 && (
            <ul className="adm-small">
              {result.errors.map((problem) => (
                <li key={problem.row} className="adm-error">
                  Row {problem.row}: {problem.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </form>
  );
}

// ─── The mailboxes of one batch ──────────────────────────────────────────────

function JobTable({
  slug,
  jobs,
  active,
  onChanged,
}: {
  slug: string;
  jobs: readonly MigrationJob[];
  active: boolean;
  onChanged: () => void;
}) {
  if (jobs.length === 0) {
    return <p className="adm-muted adm-small">No mailboxes in this import.</p>;
  }
  return (
    <div className="adm-scroll">
      <table className="adm-table">
        <thead>
          <tr>
            <th>Mailbox here</th>
            <th>Old mailbox</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Last copied</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <JobRow key={job.id} slug={slug} job={job} active={active} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobRow({
  slug,
  job,
  active,
  onChanged,
}: {
  slug: string;
  job: MigrationJob;
  active: boolean;
  onChanged: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [detail, setDetail] = useState(false);
  const route = `${base(slug)}/migrations/jobs/${encodeURIComponent(job.id)}`;
  const post = async (action: string, body?: unknown) => {
    await api(`${route}/${action}`, { method: 'POST', ...(body ? { body } : {}) });
    onChanged();
  };
  const note = statusNote(job);
  const again = job.has_credential && (job.status === 'synced' || job.status === 'failed');
  const closed = job.status === 'cancelled' || job.status === 'done';

  return (
    <>
      <tr>
        <td className="adm-mono">{job.dest_email}</td>
        <td className="adm-mono">
          {job.source_user} @ {job.source_host}
          {job.source_kind !== 'imap' && (
            <span className="adm-muted adm-small">
              {' '}
              ({job.source_kind === 'gmail' ? 'Gmail' : 'Microsoft 365'})
            </span>
          )}
        </td>
        <td>
          <StatusBadge job={job} />
          {note && <div className="adm-small adm-muted">{note}</div>}
          {job.last_error && job.status !== 'running' && (
            <div className="adm-small">
              <ErrorText text={job.last_error} />
            </div>
          )}
        </td>
        <td>
          <Progress job={job} />
        </td>
        <td>{when(job.last_synced_at)}</td>
        <td>
          <span className="adm-actions">
            {active && again && job.status === 'synced' && (
              <>
                <ActionButton tone="ghost" run={() => post('sync', { phase: 'topup' })}>
                  Top up
                </ActionButton>
                <ActionButton tone="ghost" run={() => post('sync', { phase: 'final' })}>
                  Final pass
                </ActionButton>
              </>
            )}
            {active && again && job.status === 'failed' && (
              <ActionButton tone="ghost" run={() => post('sync', { phase: 'topup' })}>
                Try again
              </ActionButton>
            )}
            {active && job.status === 'synced' && (
              <ActionButton run={() => post('complete')}>Finish</ActionButton>
            )}
            {active && !closed && (
              <ActionButton
                tone="danger"
                run={async () => {
                  if (
                    window.confirm(
                      `Cancel the import into ${job.dest_email}? Mail already copied stays; the stored password is deleted.`,
                    )
                  ) {
                    await post('cancel');
                  }
                }}
              >
                Cancel
              </ActionButton>
            )}
            {job.report && (
              <button
                type="button"
                className="adm-copy"
                aria-expanded={detail}
                onClick={() => setDetail(!detail)}
              >
                {detail ? 'Hide counts' : 'Counts'}
              </button>
            )}
          </span>
        </td>
      </tr>
      {detail && job.report && (
        <tr>
          <td colSpan={6}>
            <Reconciliation report={job.report} />
          </td>
        </tr>
      )}
    </>
  );
}

/** The last copy's counts, folder by folder: the old server against this one. */
export function Reconciliation({ report }: { report: MigrationReport }) {
  return (
    <div className="mig-report">
      <p className="adm-small">
        Last pass: {report.transferred.toLocaleString()} copied · {report.skipped.toLocaleString()}{' '}
        already here · {report.missing.toLocaleString()} still missing ·{' '}
        {report.errors.toLocaleString()} errors · {size(report.bytes)}
      </p>
      {report.folders.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Folder</th>
              <th>Old server</th>
              <th>Here</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {report.folders.map((f) => {
              const short = f.dest < f.source;
              return (
                <tr key={f.folder} className={short ? 'mig-short' : undefined}>
                  <td className="adm-mono">{f.folder}</td>
                  <td>{f.source.toLocaleString()}</td>
                  <td className={short ? 'adm-error' : undefined}>{f.dest.toLocaleString()}</td>
                  <td>
                    {short ? (
                      <Badge tone="crit">{(f.source - f.dest).toLocaleString()} not here</Badge>
                    ) : (
                      <Badge tone="ok">all here</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {report.error_lines && report.error_lines.length > 0 && (
        <div className="adm-details">
          {report.error_lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}
